// Flagship terminal modules — one SEO deep-dive page each at /terminal/<slug>.
// System keys map to icons in the page/hub components.

export const MODULE_SYSTEMS = [
  { key: "engine", name: "Trading Engine & Signals", tag: "signals" },
  { key: "discovery", name: "Screening & Discovery", tag: "discovery" },
  { key: "data", name: "Market Data & Visualization", tag: "market data" },
  { key: "intel", name: "Intelligence", tag: "intel" },
  { key: "verify", name: "Verification & Transparency", tag: "verify" },
  { key: "risk", name: "Risk Controls", tag: "risk" },
  { key: "edu", name: "Education & Community", tag: "community" },
];

export const MODULES = [
  // ── Trading Engine & Signals ──
  {
    slug: "market-scanner",
    system: "engine",
    name: "Market Scanner",
    seoTitle: "Crypto Market Scanner — Live Rule-Based Signals Across 800+ Assets",
    metaDescription:
      "TradrQuant's Market Scanner runs 16 rule-based strategies across 800+ crypto assets in real time, printing fully specified signals — entry, stop, TP1, TP2, tier and timeframe-confluence — that never repaint.",
    tagline: "Live rule-based signals across the whole crypto market — entries, stops and targets, continuously.",
    overview: [
      "The Market Scanner is the core of the TradrQuant terminal: a live, rule-based engine that watches 800+ crypto assets at once and prints structured trade signals the moment confluence forms. Every signal arrives fully specified — entry, stop-loss, TP1 and TP2, a tier rating and a timeframe-confluence (TFC) read — so there is nothing left to guess.",
      "It evaluates 16 independent strategies in parallel on our own infrastructure, not a static script frozen on a single chart. Because the logic lives server-side and resolves on confirmed candle closes, every signal is consistent, reproducible and non-repainting.",
    ],
    capabilities: [
      ["16 strategies in parallel", "Trend, momentum, reversal and structure strategies evaluated on every scanned asset simultaneously."],
      ["Fully specified signals", "Entry, SL, TP1 and TP2 attached to every signal — no ambiguous arrows to interpret."],
      ["Tier + TFC on every row", "Each signal carries a PREMIUM / STANDARD / EXPLORATORY tier and a multi-timeframe alignment score."],
      ["Server-side & non-repainting", "Runs on our pipeline against live data and resolves on confirmed closes, whether your chart is open or not."],
    ],
    fit: "The scanner is what turns one trader's chart-reading into whole-market coverage — the same detection logic behind the free TradrQuant Pro indicator, scaled to the entire universe in real time.",
    faqs: [
      { q: "How many assets does the Market Scanner cover?", a: "It watches 800+ crypto assets continuously, evaluating all 16 strategies on each — coverage no manual chartist can match." },
      { q: "Do the signals repaint?", a: "No. Signals resolve on confirmed candle closes and are logged server-side, so a printed signal never moves or disappears afterwards." },
      { q: "Is the Market Scanner available yet?", a: "It ships through the founder early-release. Create a free account to get access as modules roll out." },
    ],
  },
  {
    slug: "signal-tiers",
    system: "engine",
    name: "Signal Tiers",
    seoTitle: "Signal Tiers — PREMIUM / STANDARD / EXPLORATORY Confluence Grading",
    metaDescription:
      "TradrQuant grades every signal PREMIUM, STANDARD or EXPLORATORY by strategy confluence, so you can filter for exactly the conviction level that fits your risk.",
    tagline: "Every signal graded by conviction, so you trade only the quality you want.",
    overview: [
      "Signal Tiers turn a raw trigger into a graded read of how much agreement sits behind it. Each signal is classified PREMIUM, STANDARD or EXPLORATORY based on how many strategies and structural conditions align at the moment it prints.",
      "Instead of treating every alert the same, you can filter the whole scanner down to the conviction band that matches your style — high-conviction PREMIUM only, or a wider net when you're hunting early setups.",
    ],
    capabilities: [
      ["Three conviction bands", "PREMIUM (highest confluence), STANDARD (baseline agreement) and EXPLORATORY (early / developing)."],
      ["Confluence-driven", "Tier is assigned by how many strategies and factors agree — not a single indicator."],
      ["One-click filtering", "Show only the tiers you trade and mute the rest across the entire scanned universe."],
      ["Consistent grading", "The same rules grade every asset the same way, removing discretionary bias."],
    ],
    fit: "Tiering is the layer that makes whole-market scanning usable — it lets you scale coverage up to 800+ assets without drowning in low-quality alerts.",
    faqs: [
      { q: "What decides a PREMIUM signal?", a: "PREMIUM is reserved for the strictest confluence — the setups where the most strategies and structural factors agree at once." },
      { q: "Can I trade only PREMIUM signals?", a: "Yes. Tiers are filterable, so you can restrict the scanner to a single conviction band across every asset." },
      { q: "Is a higher tier a guarantee?", a: "No. Tiers rank structural conviction, not certainty — trading always involves risk and outcomes are never guaranteed." },
    ],
  },
  {
    slug: "tfc-system",
    system: "engine",
    name: "TFC System",
    seoTitle: "Timeframe Confluence (TFC) — HTF / MTF / LTF Alignment Scoring",
    metaDescription:
      "The TFC System scores higher-, medium- and lower-timeframe agreement on every TradrQuant signal, so entries line up with the dominant trend instead of fighting it.",
    tagline: "Higher, medium and lower timeframes, scored into one alignment read.",
    overview: [
      "The Timeframe Confluence (TFC) System checks whether the higher (HTF), medium (MTF) and lower (LTF) timeframes agree before a signal is trusted. It condenses that read into a single alignment score attached to every row in the scanner.",
      "Trading with full timeframe agreement keeps entries pointed in the direction of the dominant trend and cuts down on counter-trend noise — one of the biggest sources of avoidable losses for retail traders.",
    ],
    capabilities: [
      ["Three-tier alignment", "HTF, MTF and LTF each read independently, then combined into one score."],
      ["On every signal", "TFC travels with each scanner row so you instantly see whether the timeframes agree."],
      ["Trend-first entries", "Prioritise setups where the higher timeframe backs the lower-timeframe trigger."],
      ["Noise reduction", "De-prioritise signals that fight the dominant regime."],
    ],
    fit: "TFC is the context layer under every signal — the same multi-timeframe logic explored in our research, applied automatically across the whole market.",
    faqs: [
      { q: "What does TFC stand for?", a: "Timeframe Confluence — the degree to which higher, medium and lower timeframes agree on direction and regime." },
      { q: "Why does timeframe agreement matter?", a: "Entries aligned with the higher timeframe tend to face less counter-trend pressure than trades that fight the dominant trend." },
      { q: "Can I require full TFC agreement?", a: "Yes — the scanner lets you prioritise or filter for signals with full multi-timeframe alignment." },
    ],
  },
  {
    slug: "signal-grading-model",
    system: "engine",
    name: "Signal Grading Model (SGM)",
    seoTitle: "Signal Grading Model (SGM) — ML Scoring in Measurement Mode",
    metaDescription:
      "The Signal Grading Model is TradrQuant's machine-learning layer that scores signals in measurement mode — evaluated against real outcomes before it ever influences a live decision.",
    tagline: "A machine-learning score, measured against reality before it's trusted.",
    overview: [
      "The Signal Grading Model (SGM) is a machine-learning layer that scores each signal on top of the rule-based engine. Crucially, it runs in measurement mode: its scores are recorded and evaluated against real outcomes before they are ever allowed to influence a live decision.",
      "This is the disciplined way to introduce ML into trading — prove it on out-of-sample results first, then promote it only if it demonstrably adds edge. No black-box score is trusted on faith.",
    ],
    capabilities: [
      ["ML scoring layer", "A learned score that sits on top of the transparent rule-based signals."],
      ["Measurement mode", "Scores are logged and benchmarked against real outcomes before going live."],
      ["Outcome-anchored", "Every prediction is checked against what actually happened, not a backtest fantasy."],
      ["Promotion by proof", "A model earns influence only after it demonstrates edge on unseen data."],
    ],
    fit: "SGM is how the terminal improves over time without becoming a mystery box — it complements the rules rather than replacing the logic you can inspect.",
    faqs: [
      { q: "What is measurement mode?", a: "The model scores signals but those scores are only recorded and evaluated — they don't drive live decisions until proven." },
      { q: "Is this a black-box AI score?", a: "No. It sits on top of transparent rule-based signals and must prove edge on real outcomes before it's trusted." },
      { q: "When does SGM go live?", a: "Only after out-of-sample results justify it. Until then it runs purely as a measured, benchmarked layer." },
    ],
  },
  {
    slug: "shadow-live-fire",
    system: "engine",
    name: "Shadow / Live-Fire System",
    seoTitle: "Shadow / Live-Fire — Safe Forward-Testing for New Signal Gates",
    metaDescription:
      "TradrQuant's Shadow / Live-Fire system forward-tests new signal gates in shadow mode against live data before they ever reach users — so only proven logic ships.",
    tagline: "New logic proves itself on live markets before it ever reaches you.",
    overview: [
      "The Shadow / Live-Fire System lets new strategies and gates run silently against live market data — in shadow — before they are promoted to production. They generate and resolve signals in real time, but those signals stay internal until the data justifies going live.",
      "It's the trading equivalent of a staging environment: nothing hits users until it has been forward-tested on real, unseen markets, not just historical backtests.",
    ],
    capabilities: [
      ["Shadow mode", "New gates run live-but-silent, generating real-time signals for evaluation only."],
      ["Forward-tested", "Logic is judged on live out-of-sample data, not curve-fit history."],
      ["Controlled promotion", "A gate is only moved to live-fire once its shadow record holds up."],
      ["No user exposure", "Unproven logic can never reach the production signal feed."],
    ],
    fit: "This is the safety rail behind every improvement to the engine — it's why new features can ship confidently without risking the live signal quality traders rely on.",
    faqs: [
      { q: "What is shadow mode?", a: "A new gate runs against live data and produces signals internally, but those signals are not shown to users until proven." },
      { q: "How is this different from backtesting?", a: "Shadow mode tests on live, unseen markets in real time — it catches issues backtests on historical data can miss." },
      { q: "Does shadow logic affect my signals?", a: "No. Only logic that has passed shadow evaluation is promoted to the live feed." },
    ],
  },
  {
    slug: "audit-system",
    system: "engine",
    name: "Audit System v2",
    seoTitle: "Audit System v2 — Fill-Required, Real-Cost Signal Outcome Resolution",
    metaDescription:
      "TradrQuant's Audit System v2 resolves every signal honestly — fill-required, with per-signal-type horizons and real trading costs — so the track record reflects reality, not optimism.",
    tagline: "Honest outcome resolution — fills required, real costs, no cherry-picking.",
    overview: [
      "Audit System v2 is the accountability engine. It resolves every signal's outcome under strict, honest rules: an entry must actually be fillable, each signal type is judged over its own appropriate horizon, and results are measured with real trading costs baked in.",
      "This is what separates a credible track record from a marketing chart. By requiring fills and accounting for cost, the audit reflects what a real trader could have achieved — not an idealised backtest.",
    ],
    capabilities: [
      ["Fill-required", "An outcome only counts if the entry was realistically achievable."],
      ["Per-signal-type horizons", "Each strategy is evaluated over a window that fits how it actually trades."],
      ["Real-cost accounting", "Fees and slippage assumptions are included so results aren't inflated."],
      ["Immutable record", "Resolved outcomes are logged and feed the public verification layer."],
    ],
    fit: "The audit is the backbone of TradrQuant's transparency promise — it's what lets every resolved signal be published and independently checked.",
    faqs: [
      { q: "What does fill-required mean?", a: "A signal only resolves as a real outcome if its entry price was actually achievable in the live market." },
      { q: "Are trading costs included?", a: "Yes. Audit v2 accounts for real costs so the recorded performance reflects reality rather than a frictionless backtest." },
      { q: "Can I verify the results?", a: "Yes — resolved signals feed the public Stats / Verify layer with links you can independently check." },
    ],
  },

  // ── Screening & Discovery ──
  {
    slug: "screener-heat-grid",
    system: "discovery",
    name: "Screener / Heat Grid",
    seoTitle: "Crypto Screener & Heat Grid — Dual-Timeframe Volume Spike Ranking",
    metaDescription:
      "TradrQuant's Screener and Heat Grid rank the whole crypto market by dual-timeframe volume spikes, surfacing where real activity is building before it shows up on price.",
    tagline: "The whole market, ranked by where volume is actually firing.",
    overview: [
      "The Screener / Heat Grid ranks the entire crypto market by dual-timeframe volume spikes, giving you a single view of where genuine activity is concentrating right now. Volume leads price, so the grid surfaces momentum before it becomes obvious.",
      "Rather than scrolling through hundreds of charts, you get a live heat-ranked grid that pushes the most active names to the top automatically.",
    ],
    capabilities: [
      ["Dual-timeframe volume", "Combines a fast and a slow volume read to separate real spikes from noise."],
      ["Whole-market ranking", "Every scanned asset ordered by activity, refreshed live."],
      ["Heat visualisation", "A colour-coded grid that makes the hottest names instantly obvious."],
      ["Discovery-first", "Find where to look before you decide what to trade."],
    ],
    fit: "The heat grid is the discovery front-end to the scanner — it tells you where attention is flowing so the signal engine's output has context.",
    faqs: [
      { q: "What does the Heat Grid rank by?", a: "Dual-timeframe volume spikes — it highlights assets where activity is expanding on both a fast and a slow window." },
      { q: "Why volume?", a: "Volume expansion often precedes and confirms price moves, so it's a strong early tell for where setups are forming." },
      { q: "Does it cover the whole market?", a: "Yes — the grid ranks the full scanned universe, not a hand-picked watchlist." },
    ],
  },
  {
    slug: "pattern-scanner",
    system: "discovery",
    name: "Pattern Scanner",
    seoTitle: "Crypto Pattern Scanner — Live 5m Detection of Classic Chart Patterns",
    metaDescription:
      "TradrQuant's Pattern Scanner detects classic chart patterns — triple bottoms, inverse head & shoulders, flags and triangles — live on the 5-minute timeframe across the market.",
    tagline: "Classic chart patterns, detected live across the market — not by hand.",
    overview: [
      "The Pattern Scanner automatically detects classic technical patterns on the 5-minute timeframe across the scanned universe: triple bottoms, inverse head & shoulders, flags, triangles and more. It watches for the structures that traders normally hunt manually, everywhere, at once.",
      "Each detection is a starting point for analysis — a flag that a recognised structure is forming, with the context to grade it against trend and volume.",
    ],
    capabilities: [
      ["Multi-pattern detection", "Triple bottom, IH&S, flags, triangles and other well-known formations."],
      ["5m intraday focus", "Tuned to the fast timeframe where these patterns matter most for active traders."],
      ["Market-wide coverage", "Scans every asset so you never miss a forming pattern on a name you weren't watching."],
      ["Educational cross-links", "Every pattern maps to a full lesson in the TradrQuant Academy."],
    ],
    fit: "The pattern scanner pairs with the Academy — it finds live examples of the exact structures you learn to read there.",
    faqs: [
      { q: "Which patterns does it detect?", a: "Classic formations including triple bottoms, inverse head & shoulders, flags and triangles, with more added over time." },
      { q: "What timeframe does it scan?", a: "It focuses on the 5-minute chart, where these intraday patterns are most actionable." },
      { q: "Is a detected pattern a trade signal?", a: "It's a starting point, not an instruction — patterns are context to be graded against trend, volume and structure." },
    ],
  },

  // ── Market Data & Visualization ──
  {
    slug: "heatmap",
    system: "data",
    name: "Heatmap",
    seoTitle: "Crypto Market Heatmap — Order Depth, Whale Flow, Liquidations & Gamma",
    metaDescription:
      "TradrQuant's Heatmap layers resting order depth, whale flow, liquidation levels and GEX/gamma into one view — the market's hidden structure on a single screen.",
    tagline: "The market's hidden structure — depth, flow, liquidations and gamma in one view.",
    overview: [
      "The Heatmap consolidates the market's hidden structure into a single visual: resting order-book depth, whale flow, liquidation levels and GEX / gamma positioning, layered together. It's the map of where liquidity actually sits.",
      "Instead of juggling four separate tools, you see the price levels that matter — where large orders rest, where leverage is stacked, and where dealer gamma can accelerate or dampen a move.",
    ],
    capabilities: [
      ["Resting order depth", "Visualise where large limit orders sit above and below price."],
      ["Whale flow overlay", "See where outsized participants are active."],
      ["Liquidation levels", "Map the leverage that could cascade if price reaches it."],
      ["GEX / gamma context", "Overlay dealer gamma positioning that shapes volatility."],
    ],
    fit: "The heatmap is the terminal's structural x-ray — it turns the invisible plumbing of the market into levels you can actually trade around.",
    faqs: [
      { q: "What does the Heatmap show?", a: "Resting order depth, whale flow, liquidation levels and GEX/gamma — layered into one price-anchored view." },
      { q: "Why combine them?", a: "These forces interact. Seeing depth, leverage and gamma together explains why price reacts the way it does at key levels." },
      { q: "Is it real-time?", a: "It's built as a live, continuously updating view of market structure as part of the flagship terminal." },
    ],
  },
  {
    slug: "gex-terminal",
    system: "data",
    name: "Gamma / GEX Terminal",
    seoTitle: "Crypto GEX Terminal — BTC, ETH & SOL Options Gamma, Walls & Flip",
    metaDescription:
      "TradrQuant's GEX Terminal reads options gamma exposure for BTC/ETH (Deribit) and SOL (OKX) — call/put walls, high-volatility levels and the gamma flip that shapes price behaviour.",
    tagline: "Options gamma exposure for BTC, ETH and SOL — the walls that shape price.",
    overview: [
      "The Gamma / GEX Terminal reads the options market's gamma exposure for BTC and ETH (Deribit) and SOL (OKX). It maps call and put walls, high-volatility levels (HVL) and the all-important gamma flip — the level where dealer hedging behaviour inverts.",
      "Gamma positioning is one of the most powerful and least understood forces in crypto price action. Above the flip, dealer hedging tends to dampen volatility; below it, hedging can amplify moves. The terminal makes that structure visible.",
    ],
    capabilities: [
      ["Multi-venue GEX", "Deribit BTC/ETH and OKX SOL options exposure in one place."],
      ["Call / put walls", "The strike concentrations that often act as magnets or barriers."],
      ["Gamma flip level", "The pivot where dealer hedging flips from dampening to amplifying volatility."],
      ["High-volatility levels", "HVL zones where price tends to move fast."],
    ],
    fit: "GEX is the options-derived context layer that explains why spot behaves the way it does around key strikes — institutional intel, made readable for retail.",
    faqs: [
      { q: "What is GEX?", a: "Gamma Exposure — a measure of how options dealers must hedge, which strongly influences how spot price behaves around key strikes." },
      { q: "Which assets are covered?", a: "BTC and ETH via Deribit options, and SOL via OKX options." },
      { q: "What is the gamma flip?", a: "The level where net dealer gamma changes sign — above it hedging tends to suppress volatility, below it hedging can amplify moves." },
    ],
  },
  {
    slug: "order-wall-engine",
    system: "data",
    name: "Order Wall Engine",
    seoTitle: "Order Wall Engine — Hyperliquid L2 Order Pressure for Altcoins",
    metaDescription:
      "TradrQuant's Order Wall Engine reads Hyperliquid L2 order-book data to map real order pressure and walls for coins without deep options markets.",
    tagline: "Real order-book pressure for the coins options data can't reach.",
    overview: [
      "Not every asset has a deep options market, so the Order Wall Engine reads Hyperliquid L2 order-book data directly to map order pressure and walls for non-options coins. It's the structural read for the long tail of the market.",
      "Where the GEX Terminal covers BTC, ETH and SOL, the Order Wall Engine extends the same 'where does real liquidity sit' question to everything else, using live level-2 depth.",
    ],
    capabilities: [
      ["Hyperliquid L2 data", "Direct level-2 order-book depth for granular pressure reads."],
      ["Order walls", "Identify the large resting orders that act as support or resistance."],
      ["Non-options coverage", "Structural context for assets without a usable options market."],
      ["Live pressure", "See buy/sell imbalance building in real time."],
    ],
    fit: "It's the complement to the GEX Terminal — together they give the terminal a structural read on both majors and the wider altcoin universe.",
    faqs: [
      { q: "What data does it use?", a: "Live Hyperliquid level-2 (L2) order-book data to map real resting order pressure." },
      { q: "Why is this needed alongside GEX?", a: "Most coins lack a deep options market, so order-book walls provide the structural context that gamma data can't for those assets." },
      { q: "What is an order wall?", a: "A large concentration of resting limit orders at a price level that can act as support or resistance." },
    ],
  },
  {
    slug: "liquidation-map",
    system: "data",
    name: "Liquidation Levels Map",
    seoTitle: "Crypto Liquidation Heatmap — Pan/Zoom Map of Leverage Levels",
    metaDescription:
      "TradrQuant's Liquidation Levels Map is a pan- and zoomable heatmap of price-anchored liquidation clusters, showing where leveraged positions are stacked.",
    tagline: "A live, zoomable map of where leverage is stacked to blow.",
    overview: [
      "The Liquidation Levels Map is a pan- and zoomable heatmap that anchors liquidation clusters directly to price. It shows where leveraged positions are concentrated — the levels that can trigger cascading liquidations and sharp, fast moves.",
      "Price is often drawn toward pools of leverage. Seeing where those pools sit helps you anticipate where volatility could accelerate and where moves may exhaust.",
    ],
    capabilities: [
      ["Price-anchored walls", "Liquidation clusters mapped directly onto the price axis."],
      ["Pan & zoom", "Explore levels across timeframes and price ranges interactively."],
      ["Cascade context", "Spot the zones where one liquidation can trigger the next."],
      ["Magnet levels", "Identify leverage pools price often gravitates toward."],
    ],
    fit: "It's a key layer of the heatmap suite — the leverage map that explains many of crypto's fastest, most violent moves.",
    faqs: [
      { q: "What are liquidation levels?", a: "Price levels where leveraged positions get force-closed; large clusters can trigger cascading moves." },
      { q: "How do I use the map?", a: "Pan and zoom to see where leverage is concentrated near current price — those zones often act as magnets or volatility triggers." },
      { q: "Does it predict the future?", a: "No. It maps where leverage sits; it's context for anticipating volatility, not a guarantee of direction." },
    ],
  },
  {
    slug: "whale-radar",
    system: "data",
    name: "Whale Radar",
    seoTitle: "Whale Radar — Large-Wallet Crypto Trade Tracking & Intel",
    metaDescription:
      "TradrQuant's Whale Radar tracks large-wallet trades and surfaces actionable intel on where the biggest players are positioning across the crypto market.",
    tagline: "Track the biggest players' trades as they happen.",
    overview: [
      "Whale Radar tracks large-wallet activity and surfaces intel on where outsized participants are trading. Big players move markets, and seeing their footprints in real time is one of the clearest edges available to a retail trader.",
      "Rather than reacting to price alone, you get an early read on the flow behind it — accumulation, distribution and large directional bets as they land.",
    ],
    capabilities: [
      ["Large-wallet tracking", "Monitor the trades of the market's biggest participants."],
      ["Actionable intel", "Distil raw whale activity into signals you can actually use."],
      ["Real-time footprints", "See accumulation and distribution as it happens, not after."],
      ["Market-wide", "Coverage across the assets that matter, not a single coin."],
    ],
    fit: "Whale Radar feeds the same flow philosophy as Alpha Flow and the heatmap — it's the participant layer of the terminal's market-structure view.",
    faqs: [
      { q: "What does Whale Radar track?", a: "Large-wallet trades and positioning — the footprints of the market's biggest participants, in real time." },
      { q: "How is this an edge?", a: "Large players often move price. Seeing their activity early gives context that pure price charts can't." },
      { q: "Is a free version available?", a: "A live whale-alert feed is already offered free via our Telegram channels; the full Radar is part of the terminal." },
    ],
  },
  {
    slug: "order-flow",
    system: "data",
    name: "Alpha Flow / Order Flow",
    seoTitle: "Alpha Flow — Institutional-Style Crypto Order Flow Terminal",
    metaDescription:
      "TradrQuant's Alpha Flow is an institutional-style order-flow terminal that exposes real buy/sell aggression and flow imbalance behind crypto price action.",
    tagline: "Read the aggression behind price — institutional-style order flow.",
    overview: [
      "Alpha Flow is an institutional-style order-flow terminal that exposes the buy/sell aggression and flow imbalance driving price. Where candles show what happened, order flow shows the intent behind it — who's leaning in and where.",
      "It's the kind of tape-reading tool that professional desks rely on, rebuilt in an accessible interface for retail traders who want to see beyond the candlestick.",
    ],
    capabilities: [
      ["Aggression read", "Distinguish aggressive buyers from aggressive sellers in real time."],
      ["Flow imbalance", "Spot where one side is dominating before price fully reflects it."],
      ["Institutional lens", "The tape-reading edge professional desks use, made accessible."],
      ["Terminal-native", "Integrated with the wider market-structure suite."],
    ],
    fit: "Alpha Flow is already previewed free as a live embed on the site; inside the terminal it becomes a core layer of the market-data stack.",
    faqs: [
      { q: "What is order flow?", a: "A real-time read of buy/sell aggression and imbalance — the intent behind price, not just the resulting candles." },
      { q: "Do I get a preview?", a: "Yes. A live Alpha Flow terminal is embedded free on the TradrQuant site so you can see it in action today." },
      { q: "Who is this for?", a: "Traders who want to move beyond lagging indicators and read the actual pressure driving price." },
    ],
  },

  // ── Intelligence ──
  {
    slug: "tq-intel",
    system: "intel",
    name: "TQ Intel / Fundamentals",
    seoTitle: "TQ Intel — Tier-1 Crypto News Timeline, Sentiment & Macro Events",
    metaDescription:
      "TQ Intel brings a tier-1 crypto news timeline, live sentiment and macro-event awareness into the terminal, so you never get blindsided by the story behind the chart.",
    tagline: "The story behind the chart — news, sentiment and macro, in context.",
    overview: [
      "TQ Intel is the fundamentals layer: a curated tier-1 news timeline, live sentiment read and macro-event awareness built directly into the terminal. Technicals tell you where; intel tells you why — and when to be careful.",
      "By keeping the market narrative next to the price action, it helps you avoid trading into a scheduled catalyst blind, and gives context to moves that pure charts can't explain.",
    ],
    capabilities: [
      ["Tier-1 news timeline", "A curated feed of the headlines that actually move markets."],
      ["Live sentiment", "A read on prevailing market mood alongside the tape."],
      ["Macro awareness", "Key events flagged so you know what's on the calendar."],
      ["In-terminal context", "The narrative sits next to price, not in a separate tab."],
    ],
    fit: "TQ Intel pairs with the Economic Calendar and Quant AI to form the terminal's intelligence stack — context that makes the technical signals safer to act on.",
    faqs: [
      { q: "What news does TQ Intel cover?", a: "A curated tier-1 timeline focused on the headlines and catalysts that meaningfully move crypto markets." },
      { q: "Does it include sentiment?", a: "Yes — a live sentiment read sits alongside the news so you can gauge prevailing market mood." },
      { q: "How does it help my trading?", a: "It keeps the market narrative next to price so you don't trade blind into a known catalyst." },
    ],
  },
  {
    slug: "quant-ai",
    system: "intel",
    name: "Quant AI",
    seoTitle: "Quant AI — On-Demand AI Market Briefs for Any Crypto Ticker",
    metaDescription:
      "Quant AI generates on-demand market briefs for any ticker, summarising structure, context and key levels so you can get up to speed on any asset in seconds.",
    tagline: "An instant, structured market brief for any ticker you type.",
    overview: [
      "Quant AI generates on-demand briefs for any ticker, pulling together structure, context and key levels into a concise, readable summary. Instead of manually piecing together a dozen data points, you get a fast, structured read on any asset.",
      "It's designed to accelerate your process — a first-pass analyst that surfaces what matters so you can make a faster, more informed decision.",
    ],
    capabilities: [
      ["Any-ticker briefs", "Type a symbol and get a structured summary on demand."],
      ["Context synthesis", "Combines structure, levels and market context into one read."],
      ["Fast onboarding", "Get up to speed on an unfamiliar asset in seconds."],
      ["Decision support", "A first-pass analyst that speeds up your own analysis."],
    ],
    fit: "Quant AI is the natural-language front door to the terminal's data — it turns the raw intel and market structure into a briefing you can read in seconds.",
    faqs: [
      { q: "What does Quant AI produce?", a: "A concise, structured market brief for any ticker — structure, context and key levels summarised on demand." },
      { q: "Is it giving me trade advice?", a: "No. It's an analytical aid that summarises context to speed up your own decision-making, not financial advice." },
      { q: "Which assets can I query?", a: "It's designed to brief any ticker you enter, so you can research assets you don't normally follow." },
    ],
  },
  {
    slug: "economic-calendar",
    system: "intel",
    name: "Economic Calendar",
    seoTitle: "Economic Calendar — US Macro Events with Scanner Auto-Pause",
    metaDescription:
      "TradrQuant's Economic Calendar tracks US macro events and can auto-pause the signal scanner around high-impact releases, protecting you from event-driven whipsaw.",
    tagline: "Macro events on the calendar — and the scanner pauses for them.",
    overview: [
      "The Economic Calendar tracks high-impact US macro events and integrates directly with the engine: the scanner can automatically pause around scheduled releases, protecting you from the whipsaw that so often traps traders right at the announcement.",
      "It's not just a list of dates — it's a risk control wired into the terminal, so the system respects the calendar instead of firing signals into a coin-flip.",
    ],
    capabilities: [
      ["US macro events", "The high-impact releases most likely to move risk assets."],
      ["Scanner auto-pause", "Signals can halt automatically around scheduled events."],
      ["Whipsaw protection", "Avoid trading into the erratic price action of an announcement."],
      ["Engine-integrated", "The calendar actively shapes engine behaviour, not just your awareness."],
    ],
    fit: "The calendar bridges intelligence and risk control — it's a piece of context that actively protects the signal feed rather than just informing you.",
    faqs: [
      { q: "What events does it track?", a: "High-impact US macro releases that tend to move crypto and broader risk markets." },
      { q: "What is scanner auto-pause?", a: "Around scheduled high-impact events, the scanner can automatically stop printing signals to avoid event-driven whipsaw." },
      { q: "Can I control the pause?", a: "The behaviour is part of the engine's configurable risk controls in the terminal." },
    ],
  },

  // ── Verification & Transparency ──
  {
    slug: "public-verify",
    system: "verify",
    name: "Stats / Public Verify",
    seoTitle: "Public Verify — Every Signal Auditable with Hyperliquid Links",
    metaDescription:
      "TradrQuant publishes every resolved signal with Hyperliquid verification links, so the track record is publicly auditable instead of a screenshot you have to trust.",
    tagline: "Every resolved signal, published and independently checkable.",
    overview: [
      "Stats / Public Verify is the transparency layer that makes TradrQuant accountable. Every resolved signal is published with verification links — including Hyperliquid references — so anyone can independently check the record rather than trust a marketing screenshot.",
      "In an industry full of unverifiable claims, this is the differentiator: a track record you can audit yourself, resolved under the honest rules of Audit System v2.",
    ],
    capabilities: [
      ["Public track record", "Resolved signals published for anyone to review."],
      ["Hyperliquid verification", "External links so outcomes can be independently confirmed."],
      ["Audit-backed", "Outcomes resolved under fill-required, real-cost audit rules."],
      ["No cherry-picking", "The record reflects resolved signals, not a hand-picked highlight reel."],
    ],
    fit: "Public Verify is the promise the whole terminal is built to keep — it turns 'trust us' into 'check for yourself'.",
    faqs: [
      { q: "How can signals be verified?", a: "Each resolved signal is published with verification links, including Hyperliquid references, so you can confirm outcomes yourself." },
      { q: "Who resolves the outcomes?", a: "Audit System v2 resolves them under strict fill-required, real-cost rules before they're published." },
      { q: "Is the whole record shown?", a: "The aim is transparency — resolved signals are published rather than a curated selection of winners." },
    ],
  },
  {
    slug: "admin-dashboard",
    system: "verify",
    name: "Engine Health & Benchmark Lab",
    seoTitle: "Engine Health & Benchmark Lab — Live Scoreboard & Shadow Scorecard",
    metaDescription:
      "TradrQuant's internal dashboard tracks engine health, a live scoreboard, analytics, the shadow scorecard and a benchmark lab — the control room behind the terminal's transparency.",
    tagline: "The control room — engine health, scoreboards and the benchmark lab.",
    overview: [
      "Behind the public terminal sits the operational dashboard: live engine health, a strategy scoreboard, analytics, the shadow scorecard and a benchmark lab. It's the control room where every gate is monitored and every experiment is measured.",
      "This is how the team keeps the engine honest — watching performance in real time, comparing shadow logic against live, and benchmarking every change before it reaches users.",
    ],
    capabilities: [
      ["Engine health", "Live monitoring of the pipeline and every active gate."],
      ["Strategy scoreboard", "Performance of each strategy tracked continuously."],
      ["Shadow scorecard", "Shadow-mode logic benchmarked against the live feed."],
      ["Benchmark lab", "Every proposed change measured before promotion."],
    ],
    fit: "It's the internal counterpart to Public Verify — the same rigour, applied to running the engine, that produces the transparent record traders see.",
    faqs: [
      { q: "Is this dashboard public?", a: "It's the internal operations layer; its purpose is to keep the engine measured and honest, which feeds the public verification you can see." },
      { q: "What is the shadow scorecard?", a: "A live benchmark comparing shadow-mode logic against the production feed before anything is promoted." },
      { q: "Why does this matter to me?", a: "Because rigorous internal measurement is what makes the public track record credible." },
    ],
  },

  // ── Risk Controls ──
  {
    slug: "news-pause",
    system: "risk",
    name: "News Pause / Emergency Stop",
    seoTitle: "News Pause & Emergency Stop — Automatic and Manual Signal Halts",
    metaDescription:
      "TradrQuant's News Pause and Emergency Stop halt signals automatically or manually around high-impact events and abnormal conditions, protecting you from chaos-driven trades.",
    tagline: "Halt signals — automatically or on command — when it's not worth trading.",
    overview: [
      "News Pause / Emergency Stop is the terminal's kill switch. Signals can be halted automatically around high-impact events, or stopped manually the instant conditions turn chaotic. Sometimes the best trade is no trade, and the system is built to enforce that.",
      "It's a simple idea with outsized impact: preventing the engine from firing into unpredictable, event-driven volatility is one of the cheapest ways to protect a track record.",
    ],
    capabilities: [
      ["Automatic pause", "Signals halt around scheduled high-impact events."],
      ["Manual emergency stop", "Kill the feed instantly when conditions demand it."],
      ["Event-aware", "Wired to the Economic Calendar for scheduled risk."],
      ["Capital preservation", "Avoid firing into coin-flip volatility."],
    ],
    fit: "It's the front line of the terminal's risk layer, working alongside circuit breakers and config gates to keep the engine disciplined.",
    faqs: [
      { q: "When does News Pause trigger?", a: "Automatically around high-impact scheduled events, and manually whenever conditions warrant an emergency stop." },
      { q: "Why halt signals at all?", a: "Event-driven volatility is often a coin flip. Not trading through it protects capital and the track record." },
      { q: "Can I trigger it myself?", a: "Yes — an emergency stop can be invoked manually to halt the feed instantly." },
    ],
  },
  {
    slug: "circuit-breakers",
    system: "risk",
    name: "Circuit Breakers",
    seoTitle: "Circuit Breakers — Cluster-Loss Detection for the Signal Engine",
    metaDescription:
      "TradrQuant's Circuit Breakers detect clusters of losses and trip the engine before a bad regime turns into a drawdown — automated discipline built into the terminal.",
    tagline: "Automated discipline — the engine trips itself before a bad run compounds.",
    overview: [
      "Circuit Breakers watch for cluster-loss conditions — a run of losses that signals the current regime has turned hostile — and trip the engine before that turns into a serious drawdown. It's automated discipline, the thing most traders lack under pressure.",
      "Markets change character without warning. Rather than stubbornly firing the same logic into a regime it wasn't built for, the breaker steps back and waits for conditions to normalise.",
    ],
    capabilities: [
      ["Cluster-loss detection", "Identify abnormal runs of losing signals in real time."],
      ["Automatic trip", "Pull back the engine before a bad regime compounds."],
      ["Regime-aware", "Respond to changes in market character, not just single trades."],
      ["Drawdown control", "Cap the damage a hostile regime can do."],
    ],
    fit: "Circuit breakers are the systematic version of knowing when to stop — a core reason the engine's record is built to survive bad regimes, not just good ones.",
    faqs: [
      { q: "What is cluster-loss detection?", a: "Monitoring for an abnormal run of losses that indicates the current market regime has turned unfavourable." },
      { q: "What happens when a breaker trips?", a: "The engine pulls back and pauses firing until conditions normalise, capping the drawdown." },
      { q: "Is this automatic?", a: "Yes — it's a systematic control, so discipline doesn't depend on a human reacting in the moment." },
    ],
  },
  {
    slug: "engine-config-gates",
    system: "risk",
    name: "Engine Config Gates",
    seoTitle: "Engine Config Gates — Wide-Stop Filters, Deny-Lists & Regime Gates",
    metaDescription:
      "TradrQuant's Engine Config Gates apply wide-stop filters, symbol deny-lists, BTC regime gates and alt-universe gates so only setups that meet the rules ever print.",
    tagline: "The rulebook the engine can't break — filters, deny-lists and regime gates.",
    overview: [
      "Engine Config Gates are the configurable rulebook that governs what the engine is even allowed to trade. Wide-stop filters remove setups requiring unreasonable risk, symbol deny-lists exclude untradeable names, and BTC regime and alt-universe gates keep the engine aligned with the broader market.",
      "These gates are how the system encodes discipline structurally — bad setups are filtered out before they ever become a signal, rather than relying on judgement after the fact.",
    ],
    capabilities: [
      ["Wide-stop filters", "Reject setups whose required stop is unreasonably large."],
      ["Symbol deny-lists", "Exclude illiquid or untradeable assets entirely."],
      ["BTC regime gates", "Align signals with the prevailing Bitcoin regime."],
      ["Alt-universe gates", "Control exposure across the altcoin universe by condition."],
    ],
    fit: "Config gates are where the engine's discipline is written down — the structural filters that make every other layer's output cleaner.",
    faqs: [
      { q: "What do the gates do?", a: "They filter what the engine can trade — removing wide-stop setups, denied symbols and signals that fight the BTC regime." },
      { q: "Why gate by BTC regime?", a: "Bitcoin's regime heavily influences the whole market, so aligning signals to it filters out low-probability counter-regime trades." },
      { q: "Are gates configurable?", a: "Yes — they're the engine's rulebook, tuned to keep signal quality high across conditions." },
    ],
  },

  // ── Education & Community ──
  {
    slug: "trader-academy",
    system: "edu",
    name: "Trader Academy / Mastery",
    seoTitle: "Trader Academy — Interactive Crypto Trading Course with Quizzes & XP",
    metaDescription:
      "The TradrQuant Trader Academy is a free interactive mastery program — 9 chapters of chart-reading, quizzes and XP that take you from candlesticks to confluence.",
    tagline: "From candlesticks to confluence — an interactive path, not a PDF.",
    overview: [
      "The Trader Academy is a structured, interactive mastery program: 9 chapters that build from reading a single candle to grading multi-timeframe confluence, complete with quizzes and XP to keep you progressing. It's the education layer that makes the rest of the terminal usable.",
      "Rather than a static ebook, it's a hands-on path designed to actually change how you read a chart — the same framework the tools are built on.",
    ],
    capabilities: [
      ["9 interactive chapters", "A structured curriculum from basics to advanced confluence."],
      ["Quizzes & XP", "Reinforce each concept and track your progress."],
      ["Tool-aligned", "Learn the exact framework the indicator and scanner use."],
      ["Free to start", "The Academy is already live and free on the site today."],
    ],
    fit: "The Academy is the on-ramp to the whole ecosystem — it teaches the language the pattern scanner, indicator and terminal all speak.",
    faqs: [
      { q: "How many chapters are in the Academy?", a: "Nine interactive chapters that build from candlestick basics to multi-timeframe confluence." },
      { q: "Is it free?", a: "Yes — the Trader Academy is already available free on the TradrQuant site." },
      { q: "Do I need experience to start?", a: "No. It starts from the fundamentals and progresses step by step with quizzes and XP." },
    ],
  },
  {
    slug: "telegram-alerts",
    system: "edu",
    name: "Telegram Alerts",
    seoTitle: "Telegram Alerts — Free Signal, Pattern & Whale Alerts to PRO Channels",
    metaDescription:
      "Get TradrQuant signal, pattern and whale alerts delivered to PRO Telegram channels — real-time market intel pushed straight to your phone.",
    tagline: "Signals, patterns and whale moves — pushed straight to your phone.",
    overview: [
      "Telegram Alerts deliver TradrQuant signal, pattern and whale activity straight to PRO Telegram channels, so you don't have to be glued to a screen to stay on top of the market. Real-time intel, pushed to where you already are.",
      "It's the notification layer of the ecosystem — the fastest way to receive what the engine and radar surface, with a free tier already live today.",
    ],
    capabilities: [
      ["Signal alerts", "The engine's signals delivered in real time."],
      ["Pattern alerts", "Chart-pattern detections pushed as they form."],
      ["Whale alerts", "Large-wallet activity straight to your feed."],
      ["Free channels live now", "Whale and pattern alert channels are already available free."],
    ],
    fit: "Telegram is the delivery layer for the whole system — the free channels are also the easiest first taste of what the terminal produces.",
    faqs: [
      { q: "What alerts can I get on Telegram?", a: "Signal, pattern and whale alerts, delivered to PRO channels in real time." },
      { q: "Is there a free option?", a: "Yes — free whale and pattern alert channels are already live and open to join." },
      { q: "Do I need the terminal to use them?", a: "No. The Telegram channels work today; they're also the easiest introduction to the wider ecosystem." },
    ],
  },
];

export const getModule = (slug) => MODULES.find((m) => m.slug === slug);
export const modulesBySystem = (key) => MODULES.filter((m) => m.system === key);
export const systemName = (key) => (MODULE_SYSTEMS.find((s) => s.key === key) || {}).name || "";
export const systemTag = (key) => (MODULE_SYSTEMS.find((s) => s.key === key) || {}).tag || "";

// Modules with a genuinely free offering available today (vs the paid terminal, in build).
export const FREE_SLUGS = ["order-flow", "whale-radar", "telegram-alerts", "trader-academy"];
export const isFree = (slug) => FREE_SLUGS.includes(slug);
export const systemHasFree = (key) => modulesBySystem(key).some((m) => FREE_SLUGS.includes(m.slug));
